import React, { useState, useEffect, useRef } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
    Box,
    Button,
    Modal,
    TextField,
    Typography,
    Tooltip,
    IconButton,
    Grid,
    MenuItem,
    Select,
    InputLabel,
    FormControl
} from '@mui/material';
import axios from 'axios';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 600,
    bgcolor: '#1A1A2E',
    border: '2px solid #0B0D2A',
    boxShadow: 24,
    p: 4,
};

export default function EmployeeTimecardPage() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);

    // filters
    const [filterSite, setFilterSite] = useState('all');
    const [filterEmployee, setFilterEmployee] = useState('');

    // add‐modal state
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [schools, setSchools] = useState([]);
    const [selectedSchoolId, setSelectedSchoolId] = useState('');
    const [selectedSchoolName, setSelectedSchoolName] = useState('');

    // form refs
    const employeeNameRef = useRef();
    const employeeNumberRef = useRef();
    const positionRef = useRef();
    const baseRateRef = useRef();
    const PTOBalanceRef = useRef();

    const apiEndpoint = "https://figazuresql.swancreate.com/api/swa/EmployeeTimecard";
    const schoolApiEndpoint = "https://figazuresql.swancreate.com/api/swa/SchoolMst";

    // fetch employees
    const fetchEmployees = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(apiEndpoint);
            // ensure each row has id = apiid
            setRows(data.map(item => ({ id: item.apiid, ...item })));
        } catch (err) {
            console.error(err);
        }
        setLoading(false);
    };

    // fetch schools
    const fetchSchools = async () => {
        try {
            const { data } = await axios.get(schoolApiEndpoint);
            setSchools(data.filter(s => s.IsActive));
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        fetchEmployees();
        fetchSchools();
    }, []);

    // inline edit
    const handleCellEditCommit = ({ id, field, value }) => {
        setRows(prev =>
            prev.map(r => r.id === id ? { ...r, [field]: value, edited: true } : r)
        );
    };

    // save row
    const handleSaveRow = async id => {
        const row = rows.find(r => r.id === id);
        if (!row) return;
        try {
            await axios.put(`${apiEndpoint}/${id}`, {
                ...row,
                SiteId: row.SiteId,    // ensure SiteId stays
                SiteName: row.SiteName // and SiteName
            });
            setRows(prev =>
                prev.map(r => r.id === id ? { ...r, edited: false } : r)
            );
        } catch (err) {
            console.error(err);
        }
    };

    // delete row
    const handleDeleteEmployee = async id => {
        try {
            await axios.delete(`${apiEndpoint}/${id}`);
            setRows(prev => prev.filter(r => r.id !== id));
        } catch (err) {
            console.error(err);
        }
    };

    // add new employee
    const handleAddEmployee = async e => {
        e.preventDefault();
        const newEmp = {
            EmployeeName: employeeNameRef.current.value,
            EmployeeNumber: employeeNumberRef.current.value,
            SiteId: selectedSchoolId,     // SchoolId from dropdown
            SiteName: selectedSchoolName,   // SchoolName for display
            Position: positionRef.current.value,
            BaseRate: baseRateRef.current.value,
            PTOBalance: PTOBalanceRef.current.value
        };
        try {
            await axios.post(apiEndpoint, newEmp);
            await fetchEmployees();                // reload rows with valid ids
            setAddModalOpen(false);
            setSelectedSchoolId('');
            setSelectedSchoolName('');
            [employeeNameRef, employeeNumberRef, positionRef, baseRateRef, PTOBalanceRef]
                .forEach(r => r.current && (r.current.value = ''));
        } catch (err) {
            console.error(err);
        }
    };

    // filtered rows
    const filteredRows = rows.filter(row => {
        if (filterSite !== 'all' && String(row.SiteId) !== filterSite) return false;
        const txt = filterEmployee.toLowerCase();
        if (txt) {
            const name = (row.EmployeeName || '').toLowerCase();
            const num = (row.EmployeeNumber || '').toLowerCase();
            if (!name.includes(txt) && !num.includes(txt)) return false;
        }
        return true;
    });

    // columns: only the six requested fields + actions
    const columns = [
        { field: 'EmployeeName', headerName: 'Employee Name', width: 200, editable: true },
        { field: 'EmployeeNumber', headerName: 'Employee Number', width: 150, editable: true },
        { field: 'SiteName', headerName: 'School Site', width: 200, editable: false },
        { field: 'Position', headerName: 'Position', width: 180, editable: true },
        { field: 'BaseRate', headerName: 'Base Rate', width: 120, editable: true, type: 'number' },
        { field: 'PTOBalance', headerName: 'PTO Balance', width: 140, editable: true, type: 'number' },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 150,
            renderCell: ({ id }) => (
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Save">
                        <IconButton onClick={() => handleSaveRow(id)}>
                            <SaveIcon sx={{ color: '#ADD8E6' }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <IconButton onClick={() => handleDeleteEmployee(id)}>
                            <DeleteIcon sx={{ color: '#ADD8E6' }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            )
        }
    ];

    return (
        <Box sx={{ p: 2, backgroundColor: '#1A1A2E', height: '100vh' }}>
            <Typography variant="h4" sx={{ mb: 2, color: '#ADD8E6', fontWeight: 'bold' }}>
                Employee Time Card Processing
            </Typography>

            {/* filters */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel sx={{ color: '#ADD8E6', fontWeight: 'bold' }}>School Site</InputLabel>
                    <Select
                        value={filterSite}
                        label="School Site"
                        onChange={e => setFilterSite(e.target.value)}
                        sx={{
                            color: '#ADD8E6',
                            backgroundColor: '#1A1A2E',
                            '& .MuiSelect-icon': { color: '#ADD8E6' }
                        }}
                    >
                        <MenuItem value="all"><em>All</em></MenuItem>
                        {schools.map(s => (
                            <MenuItem key={s.SchoolId} value={String(s.SchoolId)}>
                                {s.SchoolName}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField
                    label="Search Name or Number"
                    value={filterEmployee}
                    onChange={e => setFilterEmployee(e.target.value)}
                    variant="outlined"
                    size="medium"
                    sx={{
                        backgroundColor: '#1A1A2E',
                        '& .MuiInputBase-input': { color: '#ADD8E6', fontWeight: 'bold' },
                        '& .MuiInputLabel-root': { color: '#ADD8E6', fontWeight: 'bold' }
                    }}
                />
                <Button
                    variant="contained"
                    onClick={() => setAddModalOpen(true)}
                    sx={{ fontWeight: 'bold', fontSize: '1rem' }}
                >
                    Add New Employee
                </Button>
            </Box>

            {/* data grid */}
            <DataGrid
                rows={filteredRows}
                getRowId={row => row.id}
                columns={columns}
                loading={loading}
                pageSize={25}
                rowsPerPageOptions={[25, 50, 100]}
                onCellEditCommit={handleCellEditCommit}
                sx={{
                    backgroundColor: '#16213E',
                    color: '#ADD8E6',
                    border: 'none',
                    '& .MuiDataGrid-cell': { fontSize: '0.9rem' },
                    '& .MuiDataGrid-columnHeaders': { backgroundColor: '#1A1A2E' },
                    '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: '#1A1A2E' },
                    '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: '#16213E' },
                    '& .MuiDataGrid-footerContainer': { backgroundColor: '#1A1A2E' },
                }}
            />

            {/* add employee modal */}
            <Modal open={addModalOpen} onClose={() => setAddModalOpen(false)}>
                <Box sx={modalStyle}>
                    <Typography variant="h6" sx={{ mb: 2, color: '#ADD8E6', fontWeight: 'bold' }}>
                        Add New Employee
                    </Typography>
                    <Box
                        component="form"
                        onSubmit={handleAddEmployee}
                        sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                    >
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Employee Name"
                                    inputRef={employeeNameRef}
                                    required
                                    fullWidth
                                    InputLabelProps={{ style: { color: '#ADD8E6', fontWeight: 'bold' } }}
                                    sx={{ '& .MuiInputBase-input': { color: '#ADD8E6' } }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Employee Number"
                                    inputRef={employeeNumberRef}
                                    required
                                    fullWidth
                                    InputLabelProps={{ style: { color: '#ADD8E6', fontWeight: 'bold' } }}
                                    sx={{ '& .MuiInputBase-input': { color: '#ADD8E6' } }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth required>
                                    <InputLabel sx={{ color: '#ADD8E6', fontWeight: 'bold' }}>
                                        School Site
                                    </InputLabel>
                                    <Select
                                        value={selectedSchoolId}
                                        label="School Site"
                                        onChange={e => {
                                            const id = e.target.value;
                                            setSelectedSchoolId(id);
                                            const school = schools.find(s => String(s.SchoolId) === String(id));
                                            setSelectedSchoolName(school?.SchoolName || '');
                                        }}
                                        sx={{
                                            color: '#ADD8E6',
                                            backgroundColor: '#1A1A2E',
                                            '& .MuiSelect-icon': { color: '#ADD8E6' }
                                        }}
                                    >
                                        <MenuItem value="">
                                            <em>None</em>
                                        </MenuItem>
                                        {schools.map(school => (
                                            <MenuItem key={school.SchoolId} value={String(school.SchoolId)}>
                                                {school.SchoolName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Position"
                                    inputRef={positionRef}
                                    required
                                    fullWidth
                                    InputLabelProps={{ style: { color: '#ADD8E6', fontWeight: 'bold' } }}
                                    sx={{ '& .MuiInputBase-input': { color: '#ADD8E6' } }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Base Rate"
                                    type="number"
                                    inputRef={baseRateRef}
                                    required
                                    fullWidth
                                    InputLabelProps={{ style: { color: '#ADD8E6', fontWeight: 'bold' } }}
                                    sx={{ '& .MuiInputBase-input': { color: '#ADD8E6' } }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="PTO Balance"
                                    type="number"
                                    inputRef={PTOBalanceRef}
                                    required
                                    fullWidth
                                    InputLabelProps={{ style: { color: '#ADD8E6', fontWeight: 'bold' } }}
                                    sx={{ '& .MuiInputBase-input': { color: '#ADD8E6' } }}
                                />
                            </Grid>
                        </Grid>
                        <Button variant="contained" type="submit" sx={{ fontWeight: 'bold', mt: 2 }}>
                            Add Employee
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
}
